<template>
  <div class="relative" ref="bellRef">
    <!-- Bell Button -->
    <button
      ref="buttonRef"
      @click="toggleDropdown"
      class="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 relative"
      title="Notifications"
      :aria-label="unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'"
      aria-haspopup="true"
      :aria-expanded="isOpen"
    >
      <span class="material-icons text-lg" aria-hidden="true">notifications</span>
      <span
        v-if="unreadCount > 0"
        aria-hidden="true"
        class="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <!-- New-notification announcements for screen readers -->
    <span class="sr-only" aria-live="polite">{{ unreadCount > 0 ? `${unreadCount} unread notifications` : '' }}</span>

    <!-- Dropdown Panel -->
    <div
      v-if="isOpen"
      class="absolute right-0 top-full mt-2 w-[min(380px,calc(100vw-2rem))] bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-lg z-50 overflow-hidden"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-neutral-100 dark:border-neutral-700">
        <h3 class="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Notifications</h3>
        <button
          v-if="unreadCount > 0"
          @click="handleMarkAllRead"
          class="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
        >
          Mark all read
        </button>
      </div>

      <!-- Notification List -->
      <div class="max-h-[400px] overflow-y-auto">
        <div v-if="loading" class="py-8 text-center">
          <span class="text-sm text-neutral-500 dark:text-neutral-400">Loading...</span>
        </div>

        <div v-else-if="recentNotifications.length === 0" class="py-8 text-center">
          <span class="material-icons text-3xl text-neutral-300 dark:text-neutral-600" aria-hidden="true">notifications_none</span>
          <p class="mt-2 text-sm text-neutral-500 dark:text-neutral-400">No notifications</p>
        </div>

        <button
          v-else
          v-for="notif in recentNotifications"
          :key="notif.id"
          @click="handleNotificationClick(notif)"
          class="w-full text-left px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors border-b border-neutral-50 dark:border-neutral-700/50 last:border-b-0"
          :class="notif.status === 'unread' ? 'bg-primary-50/30 dark:bg-primary-900/10' : ''"
        >
          <div class="flex items-start gap-3">
            <!-- Severity Dot -->
            <span
              class="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
              aria-hidden="true"
              :class="{
                'bg-red-500': notif.severity === 'critical',
                'bg-amber-400': notif.severity === 'warning',
                'bg-blue-400': notif.severity === 'info',
              }"
            ></span>

            <div class="flex-1 min-w-0">
              <p
                class="text-sm truncate"
                :class="notif.status === 'unread'
                  ? 'font-semibold text-neutral-900 dark:text-neutral-100'
                  : 'font-medium text-neutral-600 dark:text-neutral-400'"
              >
                <span class="sr-only">{{ notif.severity }}:</span>
                {{ notif.title }}
              </p>
              <p class="text-xs text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                {{ notif.message }}
              </p>
              <p class="text-[10px] text-neutral-500 dark:text-neutral-400 mt-1">
                {{ formatTimeAgo(notif.createdAt) }}
              </p>
            </div>
          </div>
        </button>
      </div>

      <!-- Footer -->
      <div class="border-t border-neutral-100 dark:border-neutral-700 px-4 py-2.5">
        <router-link
          to="/dashboard/settings/notifications"
          @click="isOpen = false"
          class="block text-center text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
        >
          View all notifications
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import useNotifications from "@/composables/useNotifications";
import useToast from "@/composables/useToast";

export default {
  name: "NotificationBell",
  setup() {
    const router = useRouter();
    const { showToast } = useToast();
    const bellRef = ref(null);
    const buttonRef = ref(null);
    const isOpen = ref(false);
    const {
      notifications,
      unreadCount,
      loading,
      fetchNotifications,
      markRead,
      markAllRead,
      startPolling,
      stopPolling,
    } = useNotifications();

    const recentNotifications = computed(() => {
      return notifications.value.slice(0, 15);
    });

    const toggleDropdown = () => {
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        fetchNotifications({ limit: 15 });
      }
    };

    const handleNotificationClick = async (notif) => {
      if (notif.status === "unread") {
        await markRead(notif.id);
      }
      isOpen.value = false;

      if (notif.relatedEntity) {
        if (notif.relatedEntity.type === "order") {
          router.push({
            name: "OrderView",
            params: { id: notif.relatedEntity.id },
          });
        } else if (notif.relatedEntity.type === "supplier") {
          router.push({
            name: "SupplierView",
            params: { id: notif.relatedEntity.id },
          });
        }
      }
    };

    const handleMarkAllRead = async () => {
      await markAllRead();
      // useNotifications swallows request errors; on success it optimistically
      // zeroes unreadCount, so a non-zero count here means the call failed.
      if (unreadCount.value > 0) {
        showToast("error", "Failed to mark notifications as read. Please try again.");
        return;
      }
      await fetchNotifications({ limit: 15 });
    };

    const formatTimeAgo = (isoString) => {
      if (!isoString) return "";
      const date = new Date(isoString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return "yesterday";
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    };

    // Close on outside click
    const handleClickOutside = (e) => {
      if (bellRef.value && !bellRef.value.contains(e.target)) {
        isOpen.value = false;
      }
    };

    // Close on Escape and return focus to the bell button
    const handleKeydown = (e) => {
      if (e.key === "Escape" && isOpen.value) {
        isOpen.value = false;
        buttonRef.value?.focus();
      }
    };

    onMounted(() => {
      startPolling(60000);
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("keydown", handleKeydown);
    });

    onUnmounted(() => {
      stopPolling();
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleKeydown);
    });

    return {
      bellRef,
      buttonRef,
      isOpen,
      unreadCount,
      loading,
      recentNotifications,
      toggleDropdown,
      handleNotificationClick,
      handleMarkAllRead,
      formatTimeAgo,
    };
  },
};
</script>
